import { useEffect, useState } from "react";
import type { GraphyClient } from "../api/client";
import { ApiError } from "../api/client";
import type { LocalReviewStatus, PRImpact, PullRequestItem, ReviewThread } from "../api/types";
import { EmptyState, ErrorState, Loading } from "../components/states";
import { Icon } from "../components/icons";
import { T } from "../i18n";

// GY-052 PR reviews: queue + detail, changed-symbols (before/after) summary,
// downstream consumers and overlapping impact, finding + graph links. All
// analysis is pinned to the PR's (base, head) pair; a result computed against an
// earlier head is shown as stale rather than silently wrong.

interface Props {
  client: GraphyClient;
  projectId: string;
  onViewSource: (symbolId: string) => void;
  onExploreSymbol: (symbolId: string) => void;
}

export function Reviews({ client, projectId, onViewSource, onExploreSymbol }: Props) {
  const [queue, setQueue] = useState<PullRequestItem[] | null>(null);
  const [current, setCurrent] = useState<{ pr: PullRequestItem; impact: PRImpact } | null>(null);
  const [thread, setThread] = useState<ReviewThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    client
      .prs({ projectId })
      .then((d) => live && setQueue(d))
      .catch((e) => {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [client, projectId]);

  const open = async (pr: PullRequestItem) => {
    try {
      const [impact, t] = await Promise.all([
        client.prImpact({ projectId }, pr.prId),
        client.prReview({ projectId }, pr.prId),
      ]);
      setCurrent({ pr, impact });
      setThread(t);
    } catch {
      setError(true);
    }
  };

  if (error) return <ErrorState />;
  if (loading || !queue) return <Loading />;
  if (queue.length === 0) return <EmptyState message={T.reviews.none} />;

  if (current) {
    const { pr, impact } = current;
    const stale = impact.headEpoch !== pr.headEpoch || impact.headCommit !== pr.headCommit;
    return (
      <div className="wiki-article">
        <button className="link btn-icon" onClick={() => setCurrent(null)}>
          <Icon.chevron size={13} className="flip-x" />
          {T.reviews.back}
        </button>
        <div className="stat-row">
          <h2>
            #{pr.number} {pr.title}
          </h2>
          <span className="tag">{pr.state}</span>
          <span className="tag">
            {T.reviews.ci}: {pr.ciState}
          </span>
          <span className="tag">
            {T.reviews.review}: {pr.reviewState}
          </span>
        </div>
        <p className="muted">
          {T.reviews.base}: {pr.baseCommit.slice(0, 10)} → {T.reviews.head}: {pr.headCommit.slice(0, 10)} ·{" "}
          {T.reviews.pinnedTo(impact.headCommit.slice(0, 10))}
        </p>
        {stale && <div className="banner banner-error">{T.reviews.stalePinned}</div>}

        <section className="card">
          <h3>{T.reviews.diff}</h3>
          <SymbolLinks ids={impact.changedSymbols} onViewSource={onViewSource} onExplore={onExploreSymbol} />
        </section>
        <section className="card">
          <h3>{T.reviews.downstream}</h3>
          <SymbolLinks ids={impact.downstreamConsumers} onViewSource={onViewSource} onExplore={onExploreSymbol} />
        </section>
        <section className="card">
          <h3>{T.reviews.overlap}</h3>
          {impact.overlappingPrs.length === 0 ? (
            <p className="muted">{T.reviews.none2}</p>
          ) : (
            <ul className="node-list">
              {impact.overlappingPrs.map((o) => (
                <li key={o.number}>
                  <span>#{o.number}</span>
                  <span className="muted">{o.sharedSymbols.join(", ")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="card">
          <h3>{T.reviews.findings}</h3>
          {impact.findingLinks.length === 0 ? (
            <p className="muted">{T.reviews.none2}</p>
          ) : (
            <ul className="node-list">
              {impact.findingLinks.map((f) => (
                <li key={f.symbol}>
                  <button className="link" onClick={() => onViewSource(f.symbol)}>
                    {f.symbol}
                  </button>
                  <span className="muted">{f.findingIds.join(", ")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {thread && (
          <ReviewSection
            thread={thread}
            onNote={async (b) => setThread(await client.prReviewNote({ projectId, prId: pr.prId, body: b }))}
            onStatus={async (s) => setThread(await client.prReviewStatus({ projectId, prId: pr.prId, status: s }))}
            onRequest={async (r) => setThread(await client.prReviewRequest({ projectId, prId: pr.prId, reviewer: r }))}
          />
        )}
      </div>
    );
  }

  return (
    <div className="wiki-index">
      <h2>{T.reviews.queue}</h2>
      <ul className="node-list">
        {queue.map((pr) => (
          <li key={pr.prId}>
            <button className="link" onClick={() => open(pr)}>
              #{pr.number} {pr.title}
            </button>
            <span>
              <span className="tag">{pr.state}</span>
              <span className="tag">{pr.ciState}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SymbolLinks({ ids, onViewSource, onExplore }: { ids: string[]; onViewSource: (id: string) => void; onExplore: (id: string) => void }) {
  if (ids.length === 0) return <p className="muted">{T.reviews.none2}</p>;
  return (
    <ul className="node-list">
      {ids.map((id) => (
        <li key={id}>
          <span>{id}</span>
          <span>
            <button className="link" onClick={() => onViewSource(id)}>
              {T.reviews.openSource}
            </button>{" "}
            <button className="link" onClick={() => onExplore(id)}>
              {T.reviews.viewInExplorer}
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReviewSection({
  thread,
  onNote,
  onStatus,
  onRequest,
}: {
  thread: ReviewThread;
  onNote: (body: string) => Promise<void>;
  onStatus: (status: LocalReviewStatus) => Promise<void>;
  onRequest: (reviewer: string) => Promise<void>;
}) {
  const [comment, setComment] = useState("");
  const [reviewer, setReviewer] = useState("");
  const statuses: LocalReviewStatus[] = ["none", "commented", "approved_local", "changes_requested_local"];
  return (
    <section className="card">
      <h3>{T.reviews.reviewSection}</h3>
      <p className="muted">{T.reviews.localOnlyHint}</p>

      <label>
        {T.reviews.localStatus}
        <select aria-label={T.reviews.localStatus} value={thread.localStatus} onChange={(e) => void onStatus(e.target.value as LocalReviewStatus)}>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {T.reviews.status[s]}
            </option>
          ))}
        </select>
      </label>

      <h4 className="muted">{T.reviews.notes}</h4>
      <ul className="node-list">
        {thread.notes.map((n) => (
          <li key={n.noteId}>
            <span>{n.body}</span>
            <span className="muted">{n.author}</span>
          </li>
        ))}
        {thread.notes.length === 0 && <li className="muted">{T.reviews.none2}</li>}
      </ul>
      <form
        className="mcp-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (comment.trim()) void onNote(comment.trim());
          setComment("");
        }}
      >
        <label className="search">
          {T.reviews.comment}
          <input aria-label={T.reviews.comment} value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        <button type="submit" className="btn">
          {T.reviews.post}
        </button>
      </form>

      <h4 className="muted">{T.reviews.requestedReviewers}</h4>
      <p>{thread.requestedReviewers.join(", ") || T.reviews.none2}</p>
      <form
        className="mcp-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (reviewer.trim()) void onRequest(reviewer.trim());
          setReviewer("");
        }}
      >
        <label className="search">
          {T.reviews.requestReviewer}
          <input aria-label={T.reviews.requestReviewer} value={reviewer} onChange={(e) => setReviewer(e.target.value)} />
        </label>
        <button type="submit" className="btn">
          {T.reviews.post}
        </button>
      </form>

      {thread.externalPosts.length > 0 && (
        <>
          <h4 className="muted">{T.reviews.externalPosts}</h4>
          <ul className="node-list">
            {thread.externalPosts.map((e, i) => (
              <li key={i}>
                <span>{e.destination}</span>
                <span className={`tag ${e.status === "failed" ? "tag-high" : ""}`}>{e.status}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
